/**
 * Client-side build/environment helpers.
 *
 * `REACT_APP_ENV` is set to development / testing / production by the
 * matching `.env.*` file. Plain `npm start` reads `.env`, which does
 * not set it — local development is therefore treated as non-production.
 */
export const isProduction = (env = process.env.REACT_APP_ENV) => env === 'production';

/**
 * Client-side environment lock for the Permissions Testing feature.
 *
 * The backend hides the whole `/testing/permissions` API behind two
 * independent locks (environment + an App Setting) and answers 404 on
 * production. This is the client's half of the first lock: on a
 * production build the route object below is built as `{}`, so there is
 * no route entry and no nav item. The `lazy(() => import(...))` call
 * itself is still statically reachable (it sits outside the ternary),
 * so webpack emits the page's chunk on every build regardless — this
 * lock does not, and cannot, remove that chunk. The real gate is the
 * backend answering 404 for the API, backed by the `PERMISSIONS_MANAGE`
 * bit the route also requires.
 *
 * This deliberately stays a deny-list (`env === 'production'`) rather
 * than an allow-list of known non-production names: a future
 * environment (e.g. "staging") should get this tool by default, since
 * it is a non-production-only feature and the backend's own 404 plus
 * the permission bit bound the risk either way. Do not tighten this to
 * an allow-list.
 *
 * There is deliberately no "force show" escape hatch, here or anywhere.
 */
export const isPermissionsTestingEnabled = (env = process.env.REACT_APP_ENV) => !isProduction(env);

/**
 * Email to prefill the sign-in form with, while a developer is running
 * the app on their own machine. Typing the same address dozens of times
 * a day is the kind of friction that is worth one small helper.
 *
 * Two independent locks, because this writes an address into a form on
 * a login screen:
 *
 *   - `NODE_ENV === 'development'` is true only under `npm start`.
 *     Every built bundle — the dev deployment included, since
 *     `react-scripts build` pins NODE_ENV to production whatever
 *     `.env.*` says — is therefore excluded, and no deployed
 *     environment can prefill anything.
 *   - The address itself comes from `REACT_APP_DEV_LOGIN_EMAIL`,
 *     which lives in `.env.local` (git-ignored) rather than in any
 *     committed file. Nobody inherits anybody else's address, and
 *     nothing personal enters the repository.
 *
 * Only the email. The password field is never prefilled: a password
 * does not belong in a file, and a login form that arrives already
 * filled in is a habit worth not forming.
 */
export const getDevLoginEmail = ({
  nodeEnv = process.env.NODE_ENV,
  email = process.env.REACT_APP_DEV_LOGIN_EMAIL,
} = {}) => (nodeEnv === 'development' && email ? email : '');
